"use client";

import { type ComponentType, type SVGProps } from "react";

import { useGetSocialAuthUrl } from "@unreserved/hooks/use-auth";
import { useToast } from "@unreserved/hooks/use-toast";
import { SocialAuthDtoProviderEnum } from "@unreserved/sdk";
import { userApi } from "@unreserved/services/sdk-client";
import { cn } from "@unreserved/utils";

import { AppleIcon, GoogleIcon } from "@/app/_components/icons";
import { registerFcmToken } from "@/app/_libs/notifications";
import { useAnalytics } from "@/hooks/use-analytics";
import { useAuthStore } from "@/stores/auth-store";

interface SocialLoginButton {
  id: SocialAuthDtoProviderEnum;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  text: string;
}

const socialLoginButtons: SocialLoginButton[] = [
  {
    id: SocialAuthDtoProviderEnum.Google,
    icon: GoogleIcon,
    text: "Google",
  },
  {
    id: SocialAuthDtoProviderEnum.Apple,
    icon: AppleIcon,
    text: "Apple",
  },
  // {
  //   id: SocialAuthDtoProviderEnum.Facebook,
  //   icon: FacebookIcon,
  //   text: "Facebook",
  // },
];

export const SocialLogin = () => {
  const { getSocialAuthUrlMutation } = useGetSocialAuthUrl();
  const { markSocialAuthPending } = useAnalytics();
  const { trackLogin } = useAnalytics();
  const { toast } = useToast();
  const isLoading = getSocialAuthUrlMutation.isPending;
  const setUser = useAuthStore((state) => state.setUser);

  const handleSocialLogin = async (provider: SocialAuthDtoProviderEnum) => {
    const response = await getSocialAuthUrlMutation.mutateAsync({ provider });
    const status = response?.status;
    const data = response?.data;

    if (status === "Success" && data?.authorizationUrl) {
      markSocialAuthPending(provider.toLowerCase());
      window.location.href = data.authorizationUrl;
      return;
    }

    if (status !== "Success" || !data?.authorizationUrl) {
      userApi.userControllerGetProfileV1().then((profile) => {
        const userProfile = profile.data;

        if (!userProfile) {
          return;
        }

        setUser(userProfile);
        trackLogin("email", userProfile.id);
      });

      registerFcmToken();
      toast.error("Failed to get social auth URL", {
        description: response?.message ?? "Please try again.",
      });
      return;
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {socialLoginButtons.map((button) => {
        const Icon = button.icon;

        return (
          <button
            key={button.id}
            type="button"
            onClick={() => handleSocialLogin(button.id)}
            disabled={isLoading}
            className={cn(
              "flex cursor-pointer items-center justify-center gap-3 rounded lg:rounded-lg",
              "border-border-300 bg-background-0 hover:bg-background-50 border",
              "h-10 px-3 lg:h-12.5 lg:px-6",
              "transition-colors",
            )}>
            <Icon className="h-6 w-6 shrink-0" width={24} height={24} />
            <span className="text-typography-900 hidden text-sm font-normal lg:inline lg:text-base">
              {button.text}
            </span>
          </button>
        );
      })}
    </div>
  );
};
